/**
 * Deep-linked detail view for one worker timesheet (authenticated internal users).
 */

import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Button, Container, Spinner } from 'react-bootstrap';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DownloadIcon from '@mui/icons-material/Download';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { AppContext } from '../../App';
import {
  getWorkerTimesheetDetail,
  exportWorkerTimesheetExcel,
  exportWorkerTimesheetPdf,
} from '../../clients/worker_timesheet_client';
import WorkerTimesheetDetailView from '../../components/WorkerTimesheet/WorkerTimesheetDetailView';
import { showToast } from '../../config_and_helpers/helpers';
import { logInfo } from '../../config_and_helpers/logger';

const LOG_CONTEXT = 'WorkerTimesheetDetailPage';

const WorkerTimesheetDetailPage = () => {
  const { id } = useParams();
  const { token } = useContext(AppContext);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorInfo, setErrorInfo] = useState(null);
  /** Which export is in flight; each button manages its own spinner. */
  const [exportingFormat, setExportingFormat] = useState(null);
  const abortRef = useRef(null);
  const exportAbortRef = useRef(null);
  const isMountedRef = useRef(true);

  const loadDetail = useCallback(async () => {
    if (!token || !id) return;

    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setLoading(true);
    setErrorInfo(null);

    logInfo(LOG_CONTEXT, 'loadDetail start', { id });

    const result = await getWorkerTimesheetDetail(token, id, controller.signal);

    if (controller.signal.aborted) return;

    if (result.success) {
      setData(result.data);
      setErrorInfo(null);
    } else {
      setData(null);
      setErrorInfo({
        status: result.status,
        detail: result.detail || 'הבקשה נכשלה',
      });
      if (result.status === 401) {
        showToast('פג תוקף ההתחברות או שאין הרשאה.', 'error', 'top-center', 4000);
      }
    }
    setLoading(false);
  }, [token, id]);

  useEffect(() => {
    isMountedRef.current = true;
    loadDetail();
    return () => {
      isMountedRef.current = false;
      if (abortRef.current) abortRef.current.abort();
      if (exportAbortRef.current) exportAbortRef.current.abort();
    };
  }, [loadDetail]);

  /**
   * Triggers a backend-driven export of this single timesheet.
   *
   * @param {'excel'|'pdf'} format
   */
  const handleExportDetail = useCallback(
    async format => {
      if (!token || !id) return;

      if (exportAbortRef.current) {
        exportAbortRef.current.abort();
      }
      const controller = new AbortController();
      exportAbortRef.current = controller;
      setExportingFormat(format);

      const exportFn =
        format === 'pdf' ? exportWorkerTimesheetPdf : exportWorkerTimesheetExcel;
      const result = await exportFn(token, id, controller.signal);

      if (!isMountedRef.current) return;
      if (exportAbortRef.current !== controller) {
        return;
      }
      exportAbortRef.current = null;
      setExportingFormat(null);

      if (result?.canceled) return;
      if (!result?.success) {
        showToast(
          format === 'pdf'
            ? 'לא ניתן לייצא PDF. נסו שוב.'
            : 'לא ניתן לייצא אקסל. נסו שוב.',
          'error',
          'top-center',
          3000,
        );
      }
    },
    [token, id],
  );

  if (loading && !data && !errorInfo) {
    return (
      <Container className="py-5 text-center" dir="rtl" lang="he">
        <Spinner animation="border" role="status" />
      </Container>
    );
  }

  if (errorInfo && !data) {
    const status = errorInfo.status;
    let title = 'לא ניתן לטעון את הדיווח';
    let body = errorInfo.detail || 'אירעה שגיאה.';
    if (status === 404) {
      title = 'הדיווח לא נמצא';
      body = 'לא נמצא דיווח עבור מזהה זה.';
    } else if (status === 403) {
      title = 'אין הרשאה לצפייה';
      body = 'אין לך הרשאה לגשת למסך זה.';
    }

    return (
      <Container className="py-5" dir="rtl" lang="he">
        <Button as={Link} to="/worker-timesheets" variant="outline-secondary" className="mb-3">
          <ArrowBackIcon fontSize="small" className="ms-1" />
          חזרה לרשימה
        </Button>
        <h4>{title}</h4>
        <p className="text-muted">{body}</p>
      </Container>
    );
  }

  return (
    <Container fluid className="py-3" dir="rtl" lang="he">
      {/* Toolbar: back + exports only (no refresh-links control). */}
      <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
        <Button as={Link} to="/worker-timesheets" variant="outline-secondary" size="sm">
          <ArrowBackIcon fontSize="small" className="ms-1" />
          חזרה לרשימה
        </Button>
        <Button
          variant="outline-success"
          size="sm"
          onClick={() => handleExportDetail('excel')}
          disabled={exportingFormat === 'excel' || loading || !data?.id}
          aria-label="ייצוא דיווח זה לאקסל"
        >
          {exportingFormat === 'excel' ? (
            <Spinner animation="border" size="sm" className="ms-2" />
          ) : (
            <DownloadIcon fontSize="small" className="ms-1" />
          )}
          ייצוא אקסל
        </Button>
        <Button
          variant="outline-danger"
          size="sm"
          onClick={() => handleExportDetail('pdf')}
          disabled={exportingFormat === 'pdf' || loading || !data?.id}
          aria-label="ייצוא דיווח זה ל-PDF"
        >
          {exportingFormat === 'pdf' ? (
            <Spinner animation="border" size="sm" className="ms-2" />
          ) : (
            <PictureAsPdfIcon fontSize="small" className="ms-1" />
          )}
          ייצוא PDF
        </Button>
      </div>

      <h4 className="mb-3">דיווח #{id}</h4>

      {data && <WorkerTimesheetDetailView data={data} />}
    </Container>
  );
};

export default WorkerTimesheetDetailPage;
